/* eslint-disable jsx-a11y/no-noninteractive-element-interactions */
import React, { useState, useEffect } from 'react';
import { Carousel } from 'react-responsive-carousel';
import 'react-responsive-carousel/lib/styles/carousel.min.css';

import ArrowForCarousel from 'src/components/ui/ArrowForCarousel';
import MediumComponent from 'src/components/other/MediumComponent';
import Image from 'src/components/ui/Image';
import Video from 'src/components/ui/Video';

import { colours } from 'src/constants/colours';

import type { EmbedSource, Medium } from 'src/types/__generated__/graphql';
import { MediumType } from 'src/types/__generated__/graphql';
import stopPropagation from 'src/utils/stopPropagation';
import CustomAudio from 'src/components/other/CustomAudio';
import getMediaThumbnail from 'src/utils/getMediaThumbnail';
import GlobalAudio from 'src/components/other/GlobalAudio';
import type { GlobalAudioContent } from 'src/contexts/AudioProvider';
import { styledComponent, styledTag } from 'src/utils/styled';
import EmbedVideo from 'src/components/ui/EmbedVideo';
import useDynamicLogEvents from 'src/hooks/useDynamicLogEvents';
import { AnalyticsEvent } from 'src/utils/analytics';

interface AdditionnalCarouselProps {
    $objectFit: 'cover' | 'contain';
    $fixedArrows: boolean;
}

const StyledCarousel = styledComponent(Carousel)<AdditionnalCarouselProps>`
    height: 100%;
    width: 100%;
    position: relative;
    .carousel-slider,
    .slider-wrapper {
        height: 100%;
        width: 100%;
        .slider {
            height: 100%;
            width: 100%;
            .slide {
                height: 100%;
                display: flex;
                justify-content: center;
                width: 100%;
                & > *:not(iframe) {
                    width: 100%;
                    height: 100%;
                }
                & > iframe {
                    width: calc(100% - 4rem);
                    height: 100%;
                }
                & > div > span,
                > div > span > span {
                    width: 100% !important;
                    height: 100% !important;
                }
                img {
                    width: 100%;
                    height: 100%;
                    object-fit: ${({ $objectFit }) => $objectFit};
                    object-position: center center;
                    background-color: ${colours.black.main};
                }
            }
        }
    }
    .control-dots {
        margin: 0;
        padding: 0.5rem 0;
    }
    .control-dots .dot {
        width: 0.375rem;
        height: 0.375rem;
        margin: 0 0.25rem;
        box-shadow: none;
        opacity: .4;
    }
    .carousel .control-dots .dot {
        box-shadow: none;
        opacity: .4;
    }
    .control-dots .dot.selected {
        width: 0.5rem;
        height: 0.5rem;
        box-shadow: none;
    }
    .control-dots .dot:focus {
        outline: none;
    }
    .carousel-arrow {
        display: ${(props) => (props.$fixedArrows ? 'flex' : 'none')};
    }
    :hover .carousel-arrow {
        display: flex;
    }
    video {
        position: absolute;
        top: 0;
        bottom: 0;
        left: 0;
        right: 0;
        width: 100%;
        height: 100%;
        object-fit: contain;
    }
    video::-webkit-media-controls-panel {
        width: 100%;
    }
    .carousel-status {
        background-color: ${colours.primary.dark};
        padding: 0.375rem 0.5rem;
        font-family: Lato;
        font-style: normal;
        font-weight: normal;
        font-size: 1rem;
        line-height: 1.25rem;
        margin: 0;
        bottom: 1.5rem;
        right: 1rem;
        top: unset;
        left: unset;
        border-radius: 0.25rem;
    }
`;

export const ZoomableImage = styledTag('div')`
    width: 100%;
    height: 100%;
    transition: transform 250ms;
    &:hover {
        transform: scale(1.1);
    }
`;

type Props = {
    media: Array<Medium | string | null>;
    alt: string;
    selectedItem?: number;
    onSelectedItemChange?: (selectedItem: number) => void;
    showIndicators?: boolean;
    zoomableImages?: boolean;
    showStatus?: boolean;
    objectFit?: AdditionnalCarouselProps['$objectFit'];
    cropWidth?: number;
    fixedArrows?: AdditionnalCarouselProps['$fixedArrows'];
    showArrows?: boolean;
    autoplay?: boolean;
    interval?: number;
    infiniteLoop?: boolean;
    useGlobalAudio?: boolean;
    globalAudioTitle?: string;
    globalAudioContent?: GlobalAudioContent;
};
const MediaCarousel: React.FC<Props> = ({
    media,
    alt,
    selectedItem = 0,
    onSelectedItemChange,
    showIndicators = true,
    showStatus = false,
    zoomableImages,
    objectFit = 'cover',
    cropWidth = 592,
    fixedArrows = false,
    showArrows = true,
    autoplay = false,
    interval = 3000,
    infiniteLoop = false,
    useGlobalAudio = false,
    globalAudioTitle,
    globalAudioContent,
}) => {
    const { logEventDynamic } = useDynamicLogEvents();
    const [currentItem, setCurrentItem] = useState(selectedItem);

    useEffect(() => {
        if (currentItem > media.length - 1) {
            setCurrentItem(media.length - 1);
        }
    }, [currentItem, media.length]);

    useEffect(() => {
        if (currentItem !== selectedItem) {
            setCurrentItem(selectedItem);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedItem]);

    const thumbnailForAudio =
        getMediaThumbnail(media as Medium[])?.type === MediumType.Image
            ? (getMediaThumbnail(media as Medium[]) as Medium)
            : undefined;

    const handleNextPrevClick = () => {
        const stopAudioEvent = new CustomEvent('stopmedia');
        window.dispatchEvent(stopAudioEvent);
        logEventDynamic(AnalyticsEvent.SeePicture);
    };

    return (
        <StyledCarousel
            $fixedArrows={fixedArrows}
            $objectFit={objectFit}
            autoPlay={autoplay}
            interval={interval}
            infiniteLoop={infiniteLoop}
            selectedItem={currentItem}
            onChange={(index, item) => setCurrentItem(index)}
            showArrows={showArrows}
            showStatus={showStatus}
            showThumbs={false}
            showIndicators={showIndicators && media.length > 1}
            statusFormatter={(current, total) => `${current}/${total}`}
            renderArrowPrev={(onClickHandler, hasPrev, label) =>
                hasPrev && (
                    <ArrowForCarousel
                        left="1rem"
                        side="left"
                        top="calc(50% - 1rem)"
                        onClickHandler={(ev) => {
                            stopPropagation(ev);
                            ev.preventDefault();
                            handleNextPrevClick();
                            if (onSelectedItemChange && selectedItem > 0) onSelectedItemChange(selectedItem - 1);
                            onClickHandler();
                        }}
                    />
                )
            }
            renderArrowNext={(onClickHandler, hasNext, label) =>
                hasNext && (
                    <ArrowForCarousel
                        right="1rem"
                        side="right"
                        top="calc(50% - 1rem)"
                        onClickHandler={(ev) => {
                            stopPropagation(ev);
                            ev.preventDefault();
                            handleNextPrevClick();
                            if (onSelectedItemChange) onSelectedItemChange(selectedItem + 1);
                            onClickHandler();
                        }}
                    />
                )
            }
            renderIndicator={(onClickHandler, isSelected, index) => {
                if (isSelected) {
                    return (
                        <li
                            onClick={(ev) => {
                                stopPropagation(ev);
                                ev.preventDefault();
                                onClickHandler(ev);
                            }}
                            className="dot selected"
                            value={index}
                            aria-label={`slide item ${index}`}
                        />
                    );
                } else {
                    return (
                        <li
                            onClick={(ev) => {
                                stopPropagation(ev);
                                ev.preventDefault();
                                onClickHandler(ev);
                                logEventDynamic(AnalyticsEvent.SeePicture);
                            }}
                            className="dot"
                            value={index}
                            aria-label={`slide item ${index}`}
                        />
                    );
                }
            }}
        >
            {
                media.map((medium, index) => {
                    if (medium) {
                        if (typeof medium === 'string') {
                            const img = (
                                <Image key={String(medium)} src={String(medium)} alt={`${alt} ${index}`} fill />
                            );
                            if (zoomableImages) {
                                return <ZoomableImage>{img}</ZoomableImage>;
                            }
                            return img;
                        }

                        return (
                            <MediumComponent
                                key={medium.id}
                                media={medium}
                                audio={
                                    useGlobalAudio ? (
                                        <GlobalAudio
                                            media={medium as Medium}
                                            thumbnail={thumbnailForAudio}
                                            content={globalAudioContent}
                                            title={globalAudioTitle}
                                        />
                                    ) : (
                                        <CustomAudio media={medium as Medium} thumbnail={thumbnailForAudio} />
                                    )
                                }
                                video={
                                    <Video src={medium.url} isTranscoding={Boolean(medium.isTranscoding)} controls />
                                }
                                image={
                                    zoomableImages ? (
                                        <ZoomableImage>
                                            <Image
                                                width={cropWidth}
                                                height={medium.aspectRatio ? cropWidth * medium.aspectRatio : undefined}
                                                src={medium.url}
                                                alt={`${alt} ${index}`}
                                            />
                                        </ZoomableImage>
                                    ) : (
                                        <Image
                                            width={cropWidth}
                                            height={medium.aspectRatio ? cropWidth * medium.aspectRatio : undefined}
                                            src={medium.url}
                                            alt={`${alt} ${index}`}
                                        />
                                    )
                                }
                                embed={
                                    <EmbedVideo
                                        embedId={String(medium.embedId)}
                                        embedSource={medium.embedSource as EmbedSource}
                                        height="100%"
                                        allowFullScreen
                                    />
                                }
                            />
                        );
                    }

                    return null;
                }) as React.ReactChild[]
            }
        </StyledCarousel>
    );
};

export default MediaCarousel;
